import "../../../css/ResearchQueue.css";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { message, Spin } from "antd";
import {
  getResearchRequests,
  getResearchRequest,
  validateResearchRequest,
  closeResearchRequest,
  retryResearchRequest,
  ingestResearchToRag,
  getResearchActivityLog,
} from "../../../services/polly/researchService";
import useResearchNotifications from "./useResearchNotifications";

/* ===== HELPERS ===== */

function getStatus(r) {
  return r?.researchStatus?.code || "unknown";
}

function formatAge(dateStr) {
  if (!dateStr) return "";
  const ms = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(ms / 86400000);
  if (days === 0) return "today";
  if (days === 1) return "1 day";
  if (days < 7) return days + " days";
  const weeks = Math.floor(days / 7);
  return weeks + (weeks === 1 ? " week" : " weeks");
}

function truncate(str, max = 80) {
  if (!str) return "";
  return str.length > max ? str.substring(0, max) + "\u2026" : str;
}

function answerBadge(status) {
  switch (status) {
    case "QUEUED": return <span className="rq-ws rq-ws-researching">🔍 Researching</span>;
    case "RESEARCHED": return <span className="rq-ws rq-ws-draft">📝 Draft</span>;
    case "VALIDATED": return <span className="rq-ws rq-ws-delivered">✓ Validated</span>;
    case "CLOSED": return <span className="rq-ws rq-ws-closed">— Closed</span>;
    case "RESEARCH_FAILED": return <span className="rq-ws" style={{background:'#fff1f0',color:'#cf1322'}}>✗ Failed</span>;
    default: return null;
  }
}

function docsBadge(r) {
  if (!r?.ragDocuments) return <span className="rq-ws rq-ws-none">—</span>;
  try {
    const docs = JSON.parse(r.ragDocuments);
    if (docs.length > 0) return <span className="rq-ws rq-ws-pending">📚 {docs.length} pending</span>;
  } catch {}
  return <span className="rq-ws rq-ws-none">—</span>;
}

/* ===== FILTER LOGIC ===== */

const FILTERS = [
  { key: "all", label: "All" },
  { key: "researching", label: "Researching", match: s => s === "QUEUED" },
  { key: "answer", label: "Answer Ready", match: s => s === "RESEARCHED" },
  { key: "docs", label: "Docs Ready", match: (s, r) => s === "RESEARCHED" && r?.ragDocuments },
  { key: "completed", label: "Completed", match: s => s === "VALIDATED" || s === "CLOSED" },
];

function filterRequests(requests, filterKey, searchQ) {
  let items = requests;
  const f = FILTERS.find(f => f.key === filterKey);
  if (f && f.match) items = items.filter(r => f.match(getStatus(r), r));
  if (searchQ) {
    const q = searchQ.toLowerCase();
    items = items.filter(r => (r.originalQuestion || "").toLowerCase().includes(q));
  }
  return items;
}

function countForFilter(requests, filterKey) {
  if (filterKey === "all") return requests.length;
  const f = FILTERS.find(f => f.key === filterKey);
  if (!f || !f.match) return requests.length;
  return requests.filter(r => f.match(getStatus(r), r)).length;
}

/* ===== SECTION TOGGLE ===== */

function Section({ title, badge, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rq-section">
      <div className="rq-sec-hdr" onClick={() => setOpen(!open)}>
        <div className="rq-sec-title">{title} {badge}</div>
        <span className={`rq-sec-arrow ${open ? "open" : ""}`}>▼</span>
      </div>
      <div className={`rq-sec-body ${open ? "" : "collapsed"}`}>{children}</div>
    </div>
  );
}

/* ===== MAIN COMPONENT ===== */

export default function ResearchQueue() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [searchQ, setSearchQ] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [activityLog, setActivityLog] = useState([]);
  const answerRef = useRef(null);
  const mountedRef = useRef(true);
  const fetchRequestsRef = useRef(null);
  const refreshDetailRef = useRef(null);

  useEffect(() => () => { mountedRef.current = false; }, []);

  // Real-time updates via STOMP — refresh list and detail on any event
  const handleStompEvent = useCallback((event) => {
    fetchRequestsRef.current?.();
    // If the event is for the currently open request, refresh detail
    if (event?.requestId && detail?.id && event.requestId === detail.id) {
      refreshDetailRef.current?.();
    }
  }, [detail?.id]);
  useResearchNotifications({ onEvent: handleStompEvent });

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getResearchRequests("all");
      if (!mountedRef.current) return;
      setRequests(Array.isArray(res) ? res : res?.content || []);
    } catch (err) {
      if (mountedRef.current) message.error("Failed to load research requests");
    }
    if (mountedRef.current) setLoading(false);
  }, []);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);
  fetchRequestsRef.current = fetchRequests;

  const openSlideout = async (id) => {
    setSelectedId(id);
    setDetailLoading(true);
    setDetail(null);
    setActivityLog([]);
    try {
      const [d, log] = await Promise.all([
        getResearchRequest(id),
        getResearchActivityLog(id).catch(() => []),
      ]);
      if (mountedRef.current) {
        setDetail(d);
        setActivityLog(Array.isArray(log) ? log : []);
      }
    } catch (err) {
      if (mountedRef.current) { message.error("Failed to load request detail"); setSelectedId(null); }
    }
    if (mountedRef.current) setDetailLoading(false);
  };

  const closeSlideout = () => { setSelectedId(null); setDetail(null); };

  const refreshDetail = async () => {
    if (!detail?.id) return;
    try {
      const [d, log] = await Promise.all([
        getResearchRequest(detail.id),
        getResearchActivityLog(detail.id).catch(() => []),
      ]);
      if (mountedRef.current) setActivityLog(Array.isArray(log) ? log : []);
      if (mountedRef.current) setDetail(d);
    } catch {}
  };
  refreshDetailRef.current = refreshDetail;

  /* ===== ACTIONS ===== */

  const handleSaveDraft = () => {
    if (answerRef.current) {
      // Draft is local-only for now
      message.success("Draft saved");
    }
  };

  const handleOpenSendModal = () => {
    setSendModalOpen(true);
    setConfirmText("");
  };

  const handleDeliver = async () => {
    if (!detail) return;
    const answer = answerRef.current?.innerText || detail.synthesizedAnswer || "";
    setActionLoading(true);
    try {
      await validateResearchRequest(detail.id, answer, null);
      message.success("✓ Validated & delivering");
      setSendModalOpen(false);
      await refreshDetail();
      fetchRequests();
    } catch (err) {
      message.error("Failed to validate");
    }
    if (mountedRef.current) setActionLoading(false);
  };

  const handleClose = async () => {
    if (!detail) return;
    setActionLoading(true);
    try {
      await closeResearchRequest(detail.id);
      message.success("Request closed");
      await refreshDetail();
      fetchRequests();
    } catch (err) { message.error("Failed to close"); }
    if (mountedRef.current) setActionLoading(false);
  };

  const handleRetry = async () => {
    if (!detail) return;
    setActionLoading(true);
    try {
      await retryResearchRequest(detail.id);
      message.success("Research retry initiated");
      await refreshDetail();
      fetchRequests();
    } catch (err) { message.error("Failed to retry"); }
    if (mountedRef.current) setActionLoading(false);
  };

  const handleApproveDoc = async (doc) => {
    if (!detail) return;
    setActionLoading(true);
    try {
      await ingestResearchToRag(detail.id, [doc]);
      message.success("✓ Document added to vault");
      await refreshDetail();
      fetchRequests();
    } catch (err) { message.error("Failed to ingest"); }
    if (mountedRef.current) setActionLoading(false);
  };

  const handleApproveAllDocs = async (docs) => {
    if (!detail) return;
    setActionLoading(true);
    try {
      await ingestResearchToRag(detail.id, docs);
      message.success(`✓ ${docs.length} document${docs.length !== 1 ? 's' : ''} approved & queued for ingestion`);
      await refreshDetail();
      fetchRequests();
    } catch (err) { message.error("Failed to ingest"); }
    if (mountedRef.current) setActionLoading(false);
  };

  /* ===== DERIVED DATA ===== */

  const filtered = filterRequests(requests, filter, searchQ);
  const status = getStatus(detail);
  let ragDocs = [];
  try { ragDocs = detail?.ragDocuments ? JSON.parse(detail.ragDocuments) : []; } catch {}

  // Check which docs have been ingested via activity log
  const ingestedDocTitles = new Set(
    activityLog
      .filter(a => a.activityType === "RAG_INGESTION" && a.status === "COMPLETED")
      .map(a => { try { return JSON.parse(a.detail)?.docTitle; } catch { return null; } })
      .filter(Boolean)
  );
  // For waiting customers: the primary contact + any linked contacts
  const waitingCustomers = detail?.contactId ? [{ id: detail.contactId, name: detail.contactName || `Contact ${detail.contactId}` }] : [];
  const confirmOk = confirmText.trim().toUpperCase() === "SEND";

  // Channel & consent context (populated by backend research request detail)
  const channelDisplayNames = {
    touchpoint_channel_webchat: "ChatStream",
    touchpoint_channel_sms: "SMS",
    touchpoint_channel_whatsapp: "WhatsApp",
  };
  const channelLabel = detail?.channelType ? (channelDisplayNames[detail.channelType] || detail.channelType) : null;
  const channelLlmEnabled = detail?.channelLlmEnabled;
  const consentGranted = detail?.commercialConsentGranted;

  /* ===== RENDER ===== */

  return (
    <div className="rq-container">
      {/* FILTER BAR */}
      <div className="rq-filter-bar">
        {FILTERS.map(f => (
          <div key={f.key}
            className={`rq-filter-chip ${filter === f.key ? "active" : ""}`}
            onClick={() => setFilter(f.key)}>
            {f.label}
            <span className="rq-fc-badge">{countForFilter(requests, f.key)}</span>
          </div>
        ))}
        <div className="rq-filter-search">
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><circle cx="6.5" cy="6.5" r="5" stroke="#bbb" strokeWidth="1.5"/><path d="M10.5 10.5L14 14" stroke="#bbb" strokeWidth="1.5" strokeLinecap="round"/></svg>
          <input type="text" placeholder="Search questions…" value={searchQ} onChange={e => setSearchQ(e.target.value)} />
        </div>
      </div>

      {/* TABLE */}
      <div className="rq-content">
        {loading ? (
          <div style={{ textAlign: "center", padding: 60 }}><Spin /></div>
        ) : filtered.length === 0 ? (
          <div className="rq-empty">No questions match this filter.</div>
        ) : (
          <div className="rq-table-wrap">
            <table className="rq-table">
              <thead>
                <tr>
                  <th>Question</th>
                  <th>Waiting</th>
                  <th>Answer Status</th>
                  <th>Docs Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => {
                  const s = getStatus(r);
                  return (
                    <tr key={r.id} className={selectedId === r.id ? "active" : ""} onClick={() => openSlideout(r.id)}>
                      <td>
                        <div className="rq-cell-question">&ldquo;{truncate(r.originalQuestion)}&rdquo;</div>
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        {r.contactId ? `1 · ${formatAge(r.createdAt)}` : <span style={{ color: "#bbb" }}>—</span>}
                      </td>
                      <td>{answerBadge(s)}</td>
                      <td>{docsBadge(r)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="rq-pagination">
          <span className="rq-pg-info">{filtered.length} item{filtered.length !== 1 ? "s" : ""}</span>
        </div>
      </div>

      {/* SLIDEOUT */}
      {selectedId && (
        <>
          <div className="rq-slideout-backdrop" onClick={closeSlideout} />
          <div className="rq-slideout">
            {detailLoading ? (
              <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}><Spin /></div>
            ) : detail ? (
              <>
                {/* HEADER */}
                <div className="rq-slideout-header">
                  <div className="rq-slideout-header-content">
                    <div className="rq-slideout-question">&ldquo;{detail.originalQuestion}&rdquo;</div>
                    <div className="rq-slideout-chips">
                      {answerBadge(status)}
                      {waitingCustomers.length > 0 && (
                        <span className="rq-chip" style={{ background: "#fff3cd", color: "#856404" }}>
                          ● {waitingCustomers.length} waiting · {formatAge(detail.createdAt)}
                        </span>
                      )}
                      {channelLabel && (
                        <span className="rq-chip" style={{
                          background: channelLlmEnabled === false ? "#fff1f0" : "#f0f5ff",
                          color: channelLlmEnabled === false ? "#cf1322" : "#1d39c4"
                        }}>
                          📱 {channelLabel}
                          {channelLlmEnabled === false && " · LLM off"}
                        </span>
                      )}
                      {consentGranted != null && (
                        <span className="rq-chip" style={{
                          background: consentGranted ? "#f6ffed" : "#fff1f0",
                          color: consentGranted ? "#3B6D11" : "#cf1322"
                        }}>
                          {consentGranted ? "✓ Consent granted" : "✗ No consent"}
                        </span>
                      )}
                    </div>
                  </div>
                  <button className="rq-slideout-close" onClick={closeSlideout}>✕</button>
                </div>

                {/* BODY */}
                <div className="rq-slideout-body">

                  {/* Waiting Customers */}
                  {waitingCustomers.length > 0 && (
                    <Section title="Waiting Customers"
                      badge={<span style={{ background: "#EFF4F9", color: "#124173", borderRadius: 3, fontSize: 10, padding: "1px 5px", fontWeight: 600 }}>{waitingCustomers.length}</span>}>
                      {waitingCustomers.map(c => (
                        <div key={c.id} className="rq-waiting-row">
                          <div className="rq-w-name">{c.name}</div>
                          <div className="rq-w-time">{formatAge(detail.createdAt)}</div>
                          <span className={`rq-w-status ${status === "VALIDATED" ? "rq-w-delivered" : "rq-w-waiting"}`}>
                            {status === "VALIDATED" ? "Delivered" : "Waiting"}
                          </span>
                        </div>
                      ))}
                    </Section>
                  )}

                  {/* Polly's Research */}
                  {status === "QUEUED" ? (
                    <Section title="Polly's Research">
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, padding: "20px 0", color: "#888" }}>
                        <div style={{ fontSize: 24 }} className="rq-pulse">🔍</div>
                        <div style={{ fontSize: 12, fontWeight: 500 }}>Gathering sources...</div>
                        <div style={{ fontSize: 11, color: "#bbb", textAlign: "center", lineHeight: 1.5 }}>
                          Polly is searching the web and<br />knowledge base for relevant information
                        </div>
                      </div>
                    </Section>
                  ) : detail.keyFindings ? (
                    <Section title="Polly's Research">
                      <div className="rq-finding-item">
                        <div className="rq-finding-text">{detail.keyFindings}</div>
                      </div>
                    </Section>
                  ) : null}

                  {/* Answer */}
                  <Section title={<>Answer {answerBadge(status)}</>}>
                    {status === "QUEUED" ? (
                      <div style={{ fontSize: 12, color: "#999", fontStyle: "italic", padding: "8px 0" }}>
                        Polly is synthesizing an answer...
                      </div>
                    ) : status === "RESEARCHED" ? (
                      <>
                        <p style={{ fontSize: 11, color: "#888", marginBottom: 8, lineHeight: 1.5 }}>
                          Polly drafted this answer. Review, edit if needed, then deliver.
                        </p>
                        <div className="rq-answer-box" contentEditable suppressContentEditableWarning
                          ref={answerRef}
                          dangerouslySetInnerHTML={{ __html: detail.rephrasedAnswer || detail.synthesizedAnswer || "" }}
                        />
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
                          <button className="rq-btn-ghost" onClick={handleSaveDraft}>Save draft</button>
                          <button className="rq-btn-primary" onClick={handleOpenSendModal}>✓ Validate &amp; Deliver</button>
                        </div>
                      </>
                    ) : status === "VALIDATED" ? (
                      <>
                        <div className="rq-status-line rq-status-line-green" style={{ marginBottom: 8 }}>
                          ✓ Validated{detail.validatedAt ? ` on ${new Date(detail.validatedAt).toLocaleDateString()}` : ""}
                        </div>
                        <div className="rq-answer-box-readonly">{detail.validatedAnswer || detail.synthesizedAnswer}</div>
                      </>
                    ) : status === "CLOSED" ? (
                      <div style={{ fontSize: 12, color: "#888" }}>Closed without answering.</div>
                    ) : status === "RESEARCH_FAILED" ? (
                      <div style={{ fontSize: 12, color: "#cf1322" }}>Research failed. Use Retry to try again.</div>
                    ) : null}
                  </Section>

                  {/* Document Recommendations */}
                  {status === "QUEUED" ? (
                    <Section title="Document Recommendations">
                      <div style={{ fontSize: 12, color: "#999", fontStyle: "italic", padding: "4px 0" }}>
                        Polly will generate document recommendations when research is complete.
                      </div>
                    </Section>
                  ) : ragDocs.length > 0 ? (
                    <Section title="Document Recommendations"
                      badge={<span style={{ background: "#EFF4F9", color: "#124173", borderRadius: 3, fontSize: 10, padding: "1px 5px", fontWeight: 600 }}>{ragDocs.length}</span>}>
                      {ragDocs.map((doc, idx) => {
                        const isIngested = ingestedDocTitles.has(doc.title);
                        return (
                          <div key={idx} className="rq-doc-card" style={isIngested ? { opacity: 0.6 } : undefined}>
                            <input className="rq-doc-title-input" defaultValue={doc.title} disabled={isIngested}
                              style={isIngested ? { background: "#f0f8e8", color: "#3B6D11" } : undefined} />
                            <div className="rq-doc-body">{doc.content}</div>
                            <div className="rq-doc-actions">
                              {isIngested ? (
                                <span className="rq-ws rq-ws-approved">✓ Ingested</span>
                              ) : (
                                <>
                                  <button className="rq-btn-blue" onClick={() => handleApproveDoc(doc)}>Add to Vault</button>
                                  <button className="rq-btn-ghost" onClick={() => message.info("WYSIWYG editor coming in a future release")}>Edit</button>
                                  <button className="rq-btn-danger-ghost" onClick={() => message.info("Document dismissed")}>Dismiss</button>
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      {ragDocs.filter(d => !ingestedDocTitles.has(d.title)).length > 1 && (
                        <div style={{ textAlign: "right", marginTop: 10 }}>
                          <button className="rq-btn-green" onClick={() => handleApproveAllDocs(ragDocs.filter(d => !ingestedDocTitles.has(d.title)))}>✓ Approve All &amp; Ingest</button>
                        </div>
                      )}
                    </Section>
                  ) : null}
                </div>

                {/* FOOTER */}
                <div className="rq-slideout-footer">
                  <button className="rq-btn-ghost" onClick={closeSlideout}>Close</button>
                  {status === "RESEARCH_FAILED" && (
                    <button className="rq-btn-primary" onClick={handleRetry} disabled={actionLoading}>
                      Retry Research
                    </button>
                  )}
                  {status === "VALIDATED" && ragDocs.length > 0 && (
                    <button className="rq-btn-green" onClick={() => handleApproveAllDocs(ragDocs)} disabled={actionLoading}>
                      ✓ Approve All &amp; Ingest
                    </button>
                  )}
                  {status === "CLOSED" && (
                    <div style={{ fontSize: 11, color: "#888" }}>— Closed</div>
                  )}
                </div>
              </>
            ) : null}
          </div>
        </>
      )}

      {/* SEND/CONFIRM MODAL */}
      {sendModalOpen && detail && (
        <div className="rq-modal-overlay" onClick={e => { if (e.target === e.currentTarget) setSendModalOpen(false); }}>
          <div className="rq-modal">
            <div className="rq-modal-hdr">
              <h3>Confirm delivery</h3>
              <button className="rq-modal-x" onClick={() => setSendModalOpen(false)}>✕</button>
            </div>
            <div className="rq-modal-body">
              <div className="rq-recipient-banner">
                Delivering to: {waitingCustomers.map(c => c.name).join(", ")} ({waitingCustomers.length} customer{waitingCustomers.length !== 1 ? "s" : ""})
              </div>
              <p style={{ fontSize: 12, color: "#555", marginBottom: 8 }}>Answer to be delivered:</p>
              <div className="rq-answer-preview">
                {answerRef.current?.innerText || detail.rephrasedAnswer || detail.synthesizedAnswer || ""}
              </div>
              <div style={{ fontSize: 12, color: "#444", marginBottom: 6 }}>
                Type <strong>SEND</strong> to confirm delivery
              </div>
              <input className={`rq-confirm-input ${confirmOk ? "matched" : ""}`}
                placeholder="SEND" value={confirmText}
                onChange={e => setConfirmText(e.target.value)}
                autoFocus />
            </div>
            <div className="rq-modal-ftr">
              <button className="rq-btn-ghost" onClick={() => setSendModalOpen(false)}>Cancel</button>
              <button className="rq-btn-primary" disabled={!confirmOk || actionLoading} onClick={handleDeliver}>
                {actionLoading ? "Delivering..." : "Deliver to customers"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
